import { VolumeTool } from '@openlayers/tools/volume/tool-volumes'
import Feature from 'ol/Feature'
import Projection from 'ol/proj/Projection'
import { Geometry } from 'ol/geom'
import * as z from "zod"; 

// API.tsx request params
export interface demApiParams {
    dem_url: string;
    tolerance: number;
    customBaseHeight?: number;
    area: number;
    kml_segment: Blob;
}

export interface ComparisonApiParams {
    cacheUuid: string;
    area: number;
    tolerance: number;
    target: CalcSurface;
    reference: CalcSurface;
    variation: any;
    kml_segment: Blob;
}

export interface MergeSurface {
    url: string;
    bounds: Bounds,
    heatmap_url?: string;
}

export interface MergeVolumeApiParams {
    cache_uuid: string;
    project_uuid: string;
    clear_cache: boolean;
}

export interface VariationApiParams {
    reference: CalcSurface;
    target: CalcSurface;
    file: Blob;
    tolerance: number;
}

export interface SaveVolumeApiParams {
    folderUuid: string;
    file: Blob;
    fillVolume: number;
    cutVolume: number;
    baseHeight?: number;
    tolerance: number;
    area: number;
    target: CalcSurface;
    reference: CalcSurface;
}

export interface SaveVolumeParams {
    volumeTool: VolumeTool;
    area: number
    feature: Feature<Geometry>
    customBaseHeight?: number
    customTolerance?: number
}

export interface CalcFormParams {
    completeFeature: Feature<Geometry>
    segmentFeature: Feature<Geometry>
    area: number
    customBaseHeight?: number
    customTolerance?: number
    addMeasurement?: boolean
    updateMeasurement?: boolean
}

export interface SaveFormParams {
    feature: Feature<Geometry>
    area: number
    customBaseHeight?: number
    customTolerance?: number
    addMeasurement?: boolean
    updateMeasurement?: boolean
}

// API.tsx responses
export const BoundsSchema = z.object({
    x_min: z.number(),
    x_max: z.number(),
    y_min: z.number(),
    y_max: z.number(),
    z_min: z.number(),
    z_max: z.number(),
})

const SurfaceSchema = z.object({
    url: z.string(),
    alpha_url: z.string(),
    heatmap_url: z.string(),
    bounds: BoundsSchema,
});

export const StockpileResponseSchema = z.object({
    fill_volume: z.number(),
    cut_volume: z.number(),
    viz_surface: SurfaceSchema,
    tolerance: z.number(),
    color: z.array(z.tuple([z.number(), z.string()])),
    lowest_point: z.number(),
    highest_point: z.number(),
    base_height: z.number(),
})
export type StockpileResponse = z.infer<typeof StockpileResponseSchema>

export const ComparisonResponseSchema = z.object({
    fill_volume: z.number(),
    cut_volume: z.number(),
})
export type ComparisonResponse = z.infer<typeof ComparisonResponseSchema>

// get-volume.tsx

export interface CalcModelDEMVolumeParams {
    sourceProj: Projection;
    feature: Feature<Geometry>;
    tolerance: number;
    cacheUuid: string;
}

export interface GetStockpileVolumeParams {
    sourceProj: Projection;
    feature: Feature<Geometry>
    customBaseHeight?: number
    tolerance: number
    cacheUuid: string;
}

export interface HandleSegmentParams {
    segment: Feature<Geometry>;
    sourceProj: Projection;
    cacheUuid: string;
    tolerance: number;
    referenceInfo: CalcSurface;
    targetInfo: CalcSurface;
    variation: any;
}

export interface segmentResult {
    segmentFillVolume: number;
    segmentCutVolume: number;
}

export interface CutFillParams {
    feature: Feature<Geometry>;
    sourceProj: Projection;
    cacheUuid: string;
    tolerance: number;
    referenceInfo: CalcSurface;
    targetInfo: CalcSurface;
    variation: any;
}

export interface cutFillResult {
    fillVolume: number;
    cutVolume: number;
}

const CalcSurfaceSchema = z.object({
    surfaceType: z.literal('model').or(z.literal('dem')),
    surfaceModelUuid: z.string().optional(),
    surfaceDemUrl: z.string().optional(),
    text: z.string(),
});
export type CalcSurface = z.infer<typeof CalcSurfaceSchema>

const VizSurfaceSchema = z.object({
    url: z.string(),
    alpha_url: z.string(),
    heatmap_url: z.string(),
    bounds: BoundsSchema
});
export type VizSurface = z.infer<typeof VizSurfaceSchema>

export const MeasurementSchema = z.object({
    id: z.number().optional(),
    area: z.number(),
    feature: z.any(),
    fillVolume: z.number(),
    cutVolume: z.number(),
    calcReference: CalcSurfaceSchema.optional(),
    calcTarget: CalcSurfaceSchema.optional(),
    vizSurface: VizSurfaceSchema.optional(),
    heatmapUrl: z.string().optional(),
    baseHeight: z.number().optional(),
    tolerance: z.number(),
    colors: z.array(z.tuple([z.number(), z.string()])),
    highestPoint: z.number(),
    lowestPoint: z.number(),
    failure_rate: z.number(),
});
export type Measurement = z.infer<typeof MeasurementSchema>

export interface Volume {
    uuid: string;
    base_url: string;
    project_uuid: string;
    folder_uuid: string;
    cut_volume: number;
    fill_volume: number;
    target_type: string;
    tolerance: number;
    target_dem_url?: string;
    target_model_uuid?: string;
    source_type?: string;
    source_dem_url?: string;
    source_model_uuid?: string;
    base_height?: number;
}

export interface Surface {
    url: string;
    alpha_url: string;
    olFeature: Feature<Geometry>;
    bounds: Bounds;
    heatmap_url?: string;
}

export interface Bounds {
    x_min: number;
    x_max: number;
    y_min: number;
    y_max: number;
    z_min: number;
    z_max: number;
}
